import React, { useEffect, useMemo, useState } from "react";
import style from "./index.module.scss";
import TopOperations from "./components/TopOperations";
import Tools from "./components/Tools";
import CommonTable from "components/CommonTable";
import {
    ColumnProps,
    PaginationConfig,
    SortOrder,
    TableRowSelection
} from "antd/lib/table";
import { ResultMessage } from "components/ResultMessage";
import { errorMessage } from "service/service";
import { useUpdateEffect } from "react-use";
import moment from "moment";
import useRequestFn from "utils/useRequestFn";
import {
    exportQuestion,
    getMyQuestionList,
    QuestionListItem,
    QuestionType,
    QuestionTypeMap,
    QueTypeId
} from "service/answerStore";
import { Tooltip } from "antd";
import { htmlToPlain, replaceImgTag } from "utils/common";
import OverLengthColumn from "../../../components/OverLengthColumn";
import OptionsRender from "../../../components/TopicItemRenderView";
import lodash from "lodash-es";

export interface searchItem {
    batchNo?: string[]; //学期
    courseNo?: string[]; //课程编号
    search?: string; //查询内容
    queTypeId?: QueTypeId[]; //题型
    paperType?: QuestionType[]; //类别
}

interface Props {
    type?: "window" | "page";
    onCheck?: (checks: QuestionListItem[]) => void;
}

export default function MyStore({ type = "page", onCheck }: Props) {
    const sortDirections: SortOrder[] = ["descend", "ascend"];

    const [paginations, setPaginations] = useState({ pageNo: 1, pageSize: 10 });
    const [searchParams, setSearchParams] = useState<searchItem>({}); //查询条件
    const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]); //当前选中项集合
    const [dictMap] = useState<Map<string, QuestionListItem | undefined>>(
        new Map()
    );

    const [getData, { loading, data: questionData }] =
        useRequestFn(getMyQuestionList);

    async function handleSearch(data: searchItem) {
        await updateData(data);
        setPaginations({ pageNo: 1, pageSize: 10 });
    }

    async function updateData(data: searchItem) {
        const params = {
            ...paginations,
            ...data
        };

        try {
            await getData(params);
        } catch (e) {
            ResultMessage.error(errorMessage(e));
        }
    }

    useUpdateEffect(() => {
        updateData(searchParams);
    }, [paginations.pageNo, paginations.pageSize]);

    function onTableChange(pagination: PaginationConfig) {
        setPaginations({
            pageNo: pagination.current!,
            pageSize: pagination.pageSize!
        });
    }

    function handleFinish(data: searchItem) {
        setSearchParams(data);
        handleSearch(data);
        setSelectedRowKeys([]);
    }

    //分页
    const pagination = {
        total: questionData?.count || 0,
        current: paginations.pageNo,
        pageSize: paginations.pageSize,
        pageSizeOptions: ["10", "20", "50", "100"],
        showSizeChanger: true,
        hideOnSinglePage: false,
        showQuickJumper: true,
        showTotal: (total: number) => <span> 总计{total}条</span>
    };

    const rowSelection: TableRowSelection<QuestionListItem> = {
        type: "checkbox",
        selectedRowKeys,
        onChange: (selectedRowKeys: any[]) => {
            setSelectedRowKeys(selectedRowKeys);
        }
    };

    useEffect(() => {
        //使用map结构，避免切换page之后访问不到数据集合
        selectedRowKeys.forEach(questionId => {
            if (!dictMap.has(questionId)) {
                dictMap.set(
                    questionId,
                    lodash.find(questionData?.list, { questionId })
                );
            }
        });
        const _upload = selectedRowKeys.map(questionId => {
            return dictMap.get(questionId);
        });
        onCheck?.(_upload as QuestionListItem[]);
    }, [selectedRowKeys]);

    const columns: ColumnProps<QuestionListItem>[] = [
        {
            title: "题干",
            dataIndex: "questionDescription",
            ellipsis: true,
            render: (text: string) => (
                <Tooltip
                    placement="topLeft"
                    title={
                        <div
                            dangerouslySetInnerHTML={{
                                __html: htmlToPlain(text, replaceImgTag)
                            }}
                        />
                    }>
                    <div
                        className={style.question}
                        dangerouslySetInnerHTML={{
                            __html: htmlToPlain(text, replaceImgTag)
                        }}
                    />
                </Tooltip>
            )
        },
        {
            title: "题型",
            dataIndex: "queTypeDescription"
        },
        {
            title: "分类",
            dataIndex: "paperType",
            render: (text: QuestionType) => <div>{QuestionTypeMap[text]}</div>
        },
        {
            title: "所属学期",
            dataIndex: "batchName"
        },
        {
            title: "所属课程",
            dataIndex: "courseNo",
            ellipsis: true,
            render: (text: string, record: QuestionListItem) => (
                <OverLengthColumn
                    value={`[${text}-${record.courseSeq}] ${record.courseName}`}
                    width={200}
                    className={style.courseColumn}
                />
            )
        },
        {
            title: "创建时间",
            dataIndex: "createTime",
            align: "center",
            render: (text: string) => (
                <div>
                    {text ? moment(text).format("YY-MM-DD HH:mm:ss") : "--"}
                </div>
            ),
            sortDirections: sortDirections,
            sorter: (a, b) => moment(a.createTime).diff(b.createTime)
        }
    ];

    const heightProps = useMemo(() => {
        if (type === "window") {
            return {
                scroll: { y: `calc(100vh - 460px)` }
            };
        }
        if (type === "page") {
            return {
                scroll: { y: `calc(100vh - 495px)` }
            };
        }
    }, [type]);

    return (
        <div className={style.root}>
            <TopOperations onFinish={handleFinish} />

            {type === "page" && (
                <Tools
                    selectedRowKeys={selectedRowKeys}
                    updateData={() => handleSearch(searchParams)}
                    restoreSelect={setSelectedRowKeys}
                    exportData={() => exportQuestion(searchParams)}
                />
            )}

            <CommonTable
                columns={columns}
                rowKey="questionId"
                dataSource={questionData?.list || []}
                loading={loading}
                pagination={pagination}
                onTableChange={onTableChange}
                rowSelection={rowSelection}
                expandedRowKeys={questionData?.list.map(i => i.questionId)}
                {...heightProps}
                expandedRowRender={record => (
                    <OptionsRender
                        title={record.questionDescription}
                        options={record.options}
                        type={record.queTypeId}
                    />
                )}
            />
        </div>
    );
}
